import { validateLoopParents } from './validate-loop-parents.function';

import { BlockLoopSourceEnum } from '../models/block-loop-source.enum';
import { BlockType } from '../models/block-type.enum';
import { BlockModel } from '../models/block.model';
import { LoopSourceReference, SurveyInstanceIdentity } from '../models/survey-loop.interface';
import { SurveyModel } from '../models/survey.model';

const fail = (message: string): never => {
  throw new Error(`SURVEY_LOOP_CONFIGURATION_INVALID: ${message}`);
};
const validId = (id: unknown): boolean => typeof id === 'string' && id.trim().length > 0;

/** Reads permanent item indices, never current array/display positions. */
export function loopCatalogue(survey: SurveyModel, block: BlockModel): LoopSourceReference[] {
  let entries: LoopSourceReference[];
  if (block.sourceType === BlockLoopSourceEnum.Media) {
    if (!Array.isArray(block.sourceMedia)) {
      throw new Error('SURVEY_LOOP_CONFIGURATION_INVALID: Media catalogue is required');
    }
    entries = block.sourceMedia.map((media) => ({ sourceId: media?.mediaId, sourceName: media?.name, sourceIndex: media?.sourceIndex }));
  } else {
    const options =
      block.sourceType === BlockLoopSourceEnum.Options
        ? block.sourceOptions
        : survey.pages.flatMap((page) => page.questions).find((question) => question.id === block.sourceQId)?.options;
    if (!Array.isArray(options)) {
      throw new Error('SURVEY_LOOP_CONFIGURATION_INVALID: Source options are required');
    }
    entries = options.map((option) => ({
      sourceId: option?.id,
      sourceName: option?.text,
      sourceIndex: /^opt_([1-9]\d*)$/.test(option?.id) ? Number(option.id.slice(4)) : NaN
    }));
  }
  if (
    entries.some((entry) => !Number.isSafeInteger(entry.sourceIndex) || entry.sourceIndex < 1) ||
    new Set(entries.map((entry) => entry.sourceIndex)).size !== entries.length
  ) {
    throw new Error('SURVEY_LOOP_CONFIGURATION_INVALID: Source indices must be unique positive integers; option IDs must be opt_N');
  }
  // Canonical ordering keeps configuration identity independent of respondent randomization.
  return entries.sort((a, b) => a.sourceIndex - b.sourceIndex);
}

export function validateLoopContract(survey: SurveyModel): void {
  if (!Array.isArray(survey?.pages) || !Array.isArray(survey?.blocks ?? [])) {
    fail('Pages and blocks must be arrays');
  }
  const questions = survey.pages.flatMap((page) => page.questions);
  const blocks = survey.blocks ?? [];
  for (const owner of questions) {
    if (owner.lastOptionIndex !== undefined && (!Number.isSafeInteger(owner.lastOptionIndex) || owner.lastOptionIndex < 0)) {
      fail('Last option index must be a nonnegative safe integer');
    }
  }
  const loops = blocks.filter((block) => block.type === BlockType.Loop);
  const ids = [...survey.pages.map((page) => page.id), ...questions.map((question) => question?.id)];
  if (ids.some((id) => !validId(id)) || new Set(ids).size !== ids.length) {
    fail('Page and question IDs must be unique and nonempty');
  }
  if (blocks.some((block) => !validId(block.id)) || new Set(blocks.map((block) => block.id)).size !== blocks.length) {
    fail('Block IDs must be unique and nonempty');
  }
  validateLoopParents(blocks);
  const reservedIds = new Set([
    ...ids,
    ...(survey.questionsHidden ?? []).map((question) => question.id),
    ...(survey.sources ?? []).flatMap((source) => source.variables ?? []).map((variable) => variable.id)
  ]);
  const questionIds = new Set(questions.map((question) => question.id));
  const questionPositions = new Map(questions.map((question, index) => [question.id, index]));
  const assigned = new Set<string>();
  for (const block of loops) {
    if (![BlockLoopSourceEnum.Question, BlockLoopSourceEnum.Options, BlockLoopSourceEnum.Media].includes(block.sourceType)) {
      fail('Unknown loop source type');
    }
    if (!Array.isArray(block.groupedQuestionIds) || !block.groupedQuestionIds.length) {
      fail('Loop questions are required');
    }
    for (const id of block.groupedQuestionIds) {
      if (!questionIds.has(id) || assigned.has(id)) {
        fail('Invalid or overlapping loop questions');
      }
      assigned.add(id);
    }
    if (block.sourceType === BlockLoopSourceEnum.Question) {
      const sourcePosition = questionPositions.get(block.sourceQId);
      const parent = blocks.find((item) => item.type === BlockType.LoopParent && item.groupedBlockIds.includes(block.id));
      if (parent) {
        const children = loops.filter((item) => parent.groupedBlockIds.includes(item.id));
        const parentQuestions = children.flatMap((child) => child.groupedQuestionIds);
        const loopOwnedSource = loops.some((item) => item.groupedQuestionIds.includes(block.sourceQId));
        if (loopOwnedSource || sourcePosition === undefined || parentQuestions.some((id) => questionPositions.get(id) <= sourcePosition)) {
          fail(`Loop child ${block.id} requires a source question asked once before parent ${parent.id}; sibling loop answers cannot be sources`);
        }
      }
      if (sourcePosition === undefined || block.groupedQuestionIds.some((id) => questionPositions.get(id) <= sourcePosition)) {
        fail('Question source must exist outside the loop and precede every loop question');
      }
    }
    const visit = (id: string, visited: Set<string>): void => {
      if (visited.has(id)) {
        fail('Cyclic block nesting');
      }
      const child = blocks.find((item) => item.id === id);
      if (!child || child.type === BlockType.Loop) {
        fail('Nested loops are unsupported');
      }
      for (const next of child.groupedBlockIds ?? []) {
        visit(next, new Set([...visited, id]));
      }
    };
    for (const id of block.groupedBlockIds ?? []) {
      visit(id, new Set([block.id]));
    }
    const settings = block.allocation;
    if (settings !== undefined && (!settings || typeof settings !== 'object' || Array.isArray(settings))) {
      fail('Invalid allocation settings');
    }
    for (const value of [settings?.quantity, settings?.completionTarget]) {
      if (value !== undefined && (!Number.isSafeInteger(value) || value <= 0)) {
        fail('Quantity and target must be positive integers');
      }
    }
    if (block.sourceType === BlockLoopSourceEnum.Media) {
      const bindings = block.stimulusQuestionIds;
      if (
        !Array.isArray(bindings) ||
        !bindings.length ||
        new Set(bindings).size !== bindings.length ||
        bindings.some((id) => !block.groupedQuestionIds.includes(id))
      ) {
        fail('Select one or more distinct questions belonging to the media loop');
      }
    }
    const catalogue = loopCatalogue(survey, block);
    for (const questionId of block.groupedQuestionIds) {
      for (const source of catalogue) {
        if (reservedIds.has(`${questionId}_${source.sourceIndex}`)) {
          fail('Generated loop question ID collides with an authored ID');
        }
      }
    }
    if (block.lastSourceIndex !== undefined && (!Number.isSafeInteger(block.lastSourceIndex) || block.lastSourceIndex < 0)) {
      fail('Last source index must be a nonnegative safe integer');
    }
    if (
      catalogue.some((entry) => !validId(entry.sourceId) || typeof entry.sourceName !== 'string') ||
      new Set(catalogue.map((entry) => entry.sourceId)).size !== catalogue.length
    ) {
      fail('Source IDs must be nonempty and unique; source names must be strings');
    }
  }
}

/** Callers pass the frozen template IDs; generated IDs never collide with authored variables/pages. */
export function surveyInstanceIdentity(
  templateQuestionId: string,
  templatePageId: string,
  loopId: string,
  source: LoopSourceReference,
  templateIds: ReadonlySet<string>
): SurveyInstanceIdentity {
  if (!validId(source.sourceId) || !Number.isSafeInteger(source.sourceIndex) || source.sourceIndex < 1) {
    fail('Source index must be a positive one-based integer');
  }
  const questionId = `${templateQuestionId}_${source.sourceIndex}`;
  if (templateIds.has(questionId)) {
    fail('Generated loop question ID collides with an authored ID');
  }
  let pageId = `loop:${encodeURIComponent(loopId)}:${encodeURIComponent(templatePageId)}:${source.sourceIndex}`;
  while (templateIds.has(pageId)) {
    pageId = `#${pageId}`;
  }
  return { templateQuestionId, templatePageId, loopId, sourceId: source.sourceId, sourceIndex: source.sourceIndex, questionId, pageId };
}
